<script lang="ts">
  import { onMount } from "svelte";
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import { verifyEmail } from "$lib/api/endpoints";
  import { AppError } from "$lib/api/problem";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";
  import { safeReturnPath } from "$lib/auth/returnTo";
  import { track } from "$lib/telemetry/emit";

  // The verification token arrives in the URL FRAGMENT (never sent to the server by
  // the browser). Read it into memory and strip it from the address bar and history
  // immediately, before any other code — or an extension/analytics hook — can see it.
  // Clicking the emailed link is the confirmation. The token is transmitted exactly
  // once to /v1/auth/email/verify after mount; fragment placement keeps it out of the
  // initial HTTP request, access logs, and ordinary link-prefetch requests.
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token: string | null = fragment.get("token");
  const fragmentReturnTo: string | null = fragment.get("returnTo");
  if (window.location.hash) {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
  }

  let error = $state<AppError | null>(null);
  let busy = $state(false);
  let consumed = $state(false);

  const MESSAGES: Record<string, string> = {
    token_expired: "This verification link has expired. Request a new one from the sign-in page.",
    token_invalid: "This verification link is invalid or has already been used.",
    token_consumed: "This verification link has already been used.",
    verification_invalid: "This verification link is invalid, expired, or has already been used.",
    // An invitation-bound link can additionally be refused by the inviting
    // organization's network policy — that is not a broken link, so it must not read
    // like one, and the recovery path is different.
    organization_ip_denied:
      "The organization that invited you only accepts connections from approved networks, and this one isn't approved. Reconnect from your office network or company VPN and open the link again."
  };

  const networkDenied = $derived(error?.code === "organization_ip_denied");

  async function finishVerification(): Promise<void> {
    if (!token || busy || consumed) return;
    busy = true;
    error = null;
    try {
      const auth = await verifyEmail(token);
      consumed = true;
      track({ name: "auth.email.verified", outcome: "success" });
      await session.refresh();
      const destination = safeReturnPath(auth.returnTo ?? fragmentReturnTo);
      router.navigate(destination, { replace: true });
    } catch (err) {
      track({ name: "auth.email.verified", outcome: "failure" });
      if (err instanceof AppError) error = err;
    } finally {
      busy = false;
    }
  }

  onMount(() => {
    if (token) void finishVerification();
  });
</script>

<AuthLayout title="Verify your email" subtitle="Finish signing in to EpiDev.">
  {#if !token}
    <Banner tone="error" title="Verification link incomplete">
      This link is missing its verification code. Request a fresh sign-in link and open
      the newest email.
    </Banner>
    <Button href="/login" fullWidth>Back to sign in</Button>
  {:else}
    {#if error}
      <Banner
        tone={networkDenied ? "warn" : "error"}
        title={networkDenied ? "This network isn't allowed" : "We couldn't verify your email"}
        correlationId={error.correlationId}
      >
        {MESSAGES[error.code] ?? error.userMessage}
      </Banner>
      {#if networkDenied}
        <p class="verify-copy">
          Still blocked? Email <a href="mailto:support@epidev.ai">support@epidev.ai</a> — EpiDev
          support can lift a network restriction for the organization.
        </p>
      {:else}
        <Button href="/login" fullWidth>Request a new link</Button>
      {/if}
    {:else}
      <p class="verify-copy" role="status" aria-live="polite">
        Finishing your secure sign-in…
      </p>
    {/if}
  {/if}
</AuthLayout>

<style>
  .verify-copy {
    font-size: 14.5px;
    margin: 0;
  }
  .verify-copy a {
    color: var(--accent);
  }
</style>
